import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import { DocumentTextIcon, CheckCircleIcon, PlusCircleIcon, TrashIcon } from '@heroicons/react/24/outline';

import API_URL from '../config/api';

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totals, setTotals] = useState({});
  const [uninvoicedCalls, setUninvoicedCalls] = useState([]);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [manualForm, setManualForm] = useState({
    client_name: '',
    amount: '',
    notes: '',
    status: 'submitted'
  });
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchInvoices();
    fetchUninvoicedCalls();
  }, []);

  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/invoices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices || []);
        setTotals(data.totals || {});
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch only completed service calls that DON'T have invoices
  const fetchUninvoicedCalls = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/invoices/uninvoiced-calls`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUninvoicedCalls(data.serviceCalls || []);
      }
    } catch (error) {
      console.error('Error fetching uninvoiced calls:', error);
    }
  };

  const generateInvoice = async (serviceCallId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/invoices/from-service-call/${serviceCallId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ tax: 0 })
      });
      
      if (response.ok) {
        toast.success('Invoice generated successfully!');
        setShowGenerateModal(false);
        fetchInvoices();
        fetchUninvoicedCalls(); // Refresh to remove the just-invoiced call
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to generate invoice');
      }
    } catch (error) {
      toast.error('Error generating invoice');
    }
  };

  const createManualInvoice = async (e) => {
    e.preventDefault();
    
    if (!manualForm.client_name.trim() || !manualForm.amount) {
      toast.error('Please fill in client name and amount');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/invoices/manual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          client_name: manualForm.client_name.trim(),
          amount: parseFloat(manualForm.amount),
          notes: manualForm.notes.trim() || null,
          status: manualForm.status
        })
      });
      
      if (response.ok) {
        toast.success('Manual invoice created successfully!');
        setShowManualModal(false);
        setManualForm({ client_name: '', amount: '', notes: '', status: 'submitted' });
        fetchInvoices();
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to create invoice');
      }
    } catch (error) {
      toast.error('Error creating invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/invoices/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });
      
      if (response.ok) {
        toast.success('Invoice status updated');
        fetchInvoices();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const confirmDelete = (invoice) => {
    setInvoiceToDelete(invoice);
    setShowDeleteModal(true);
  };

  const deleteInvoice = async () => {
    if (!invoiceToDelete) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/invoices/${invoiceToDelete.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Invoice deleted successfully');
        setShowDeleteModal(false);
        setInvoiceToDelete(null);
        fetchInvoices();
        fetchUninvoicedCalls(); // Refresh in case this was from a service call
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to delete invoice');
      }
    } catch (error) {
      toast.error('Error deleting invoice');
    } finally {
      setDeleting(false);
    }
  };

  // Format cost as currency
  const formatCost = (cost) => {
    if (!cost && cost !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cost);
  };

  // Filter invoices based on selected status
  const filteredInvoices = statusFilter === 'all' 
    ? invoices 
    : invoices.filter(inv => inv.status === statusFilter);

  // Calculate total for all invoices
  const allTotal = Object.values(totals).reduce((sum, data) => sum + (data.amount || 0), 0);
  const allCount = Object.values(totals).reduce((sum, data) => sum + (data.count || 0), 0);

  // Status card colors
  const getStatusColor = (status) => {
    switch(status) {
      case 'submitted': return { bg: '#fef3c7', border: '#f59e0b', text: '#92400e' };
      case 'open': return { bg: '#dbeafe', border: '#3b82f6', text: '#1e40af' };
      case 'paid': return { bg: '#dcfce7', border: '#22c55e', text: '#166534' };
      case 'overdue': return { bg: '#fee2e2', border: '#ef4444', text: '#991b1b' };
      default: return { bg: '#f3f4f6', border: '#9ca3af', text: '#374151' };
    }
  };

  if (loading) return <div className="spinner"></div>;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <h1 className="page-title" style={{ fontSize: 28, fontWeight: 600 }}>Invoices</h1>
        <div style={{ display: 'flex', gap: 12 }}>
          <button 
            onClick={() => setShowManualModal(true)}
            className="btn"
            style={{ background: '#ea580c', color: 'white', padding: '10px 20px', borderRadius: 6, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <PlusCircleIcon style={{ width: 20, height: 20 }} />
            Manual Invoice
          </button>
          <button 
            onClick={() => setShowGenerateModal(true)}
            className="btn btn-primary"
            style={{ background: '#2B7FE0', color: 'white', padding: '10px 20px', borderRadius: 6, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
          >
            <DocumentTextIcon style={{ width: 20, height: 20 }} />
            Generate Invoice
          </button>
        </div>
      </div>
      
      {/* Clickable Status Filter Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        {/* All Card */}
        <div 
          onClick={() => setStatusFilter('all')}
          className="stat-card" 
          style={{ 
            background: statusFilter === 'all' ? '#2c3e50' : 'white', 
            padding: 16, 
            borderRadius: 8, 
            boxShadow: statusFilter === 'all' ? '0 4px 12px rgba(44,62,80,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            border: statusFilter === 'all' ? '2px solid #2c3e50' : '2px solid transparent'
          }}
        >
          <div style={{ fontSize: 24, fontWeight: 600, color: statusFilter === 'all' ? 'white' : '#2c3e50' }}>
            ${allTotal.toFixed(2)}
          </div>
          <div style={{ fontSize: 14, color: statusFilter === 'all' ? '#94a3b8' : '#4a5568', textTransform: 'capitalize' }}>
            All
          </div>
          <div style={{ fontSize: 12, color: statusFilter === 'all' ? '#64748b' : '#a0aec0' }}>
            {allCount} invoices
          </div>
        </div>

        {/* Status Cards */}
        {Object.entries(totals).map(([status, data]) => {
          const colors = getStatusColor(status);
          const isActive = statusFilter === status;
          return (
            <div 
              key={status} 
              onClick={() => setStatusFilter(status)}
              className="stat-card" 
              style={{ 
                background: isActive ? colors.bg : 'white', 
                padding: 16, 
                borderRadius: 8, 
                boxShadow: isActive ? `0 4px 12px ${colors.border}40` : '0 1px 3px rgba(0,0,0,0.1)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                border: isActive ? `2px solid ${colors.border}` : '2px solid transparent'
              }}
            >
              <div style={{ fontSize: 24, fontWeight: 600, color: isActive ? colors.text : '#2c3e50' }}>
                ${data.amount?.toFixed(2) || '0.00'}
              </div>
              <div style={{ fontSize: 14, color: isActive ? colors.text : '#4a5568', textTransform: 'capitalize' }}>
                {status}
              </div>
              <div style={{ fontSize: 12, color: isActive ? colors.text : '#a0aec0' }}>
                {data.count} invoices
              </div>
            </div>
          );
        })}
      </div>

      {/* Filter indicator */}
      {statusFilter !== 'all' && (
        <div style={{ 
          marginBottom: 16, 
          padding: '8px 16px', 
          background: '#f0f9ff', 
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span style={{ color: '#0369a1', fontSize: 14 }}>
            Showing <strong style={{ textTransform: 'capitalize' }}>{statusFilter}</strong> invoices ({filteredInvoices.length})
          </span>
          <button
            onClick={() => setStatusFilter('all')}
            style={{ 
              background: 'none', 
              border: 'none', 
              color: '#0369a1', 
              cursor: 'pointer',
              fontSize: 14,
              textDecoration: 'underline'
            }}
          >
            Show All
          </button>
        </div>
      )}

      <div className="card">
        <div className="card-body">
          {filteredInvoices.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <p>
                {statusFilter === 'all' 
                  ? 'No invoices yet. Complete a service call to generate an invoice!'
                  : `No ${statusFilter} invoices found.`
                }
              </p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Client</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Due Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map(invoice => (
                  <tr key={invoice.id}>
                    <td>{invoice.invoice_number}</td>
                    <td>{invoice.client_name || invoice.manual_client_name || 'N/A'}</td>
                    <td>${invoice.total}</td>
                    <td>
                      <span className={`status-badge status-${invoice.status}`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td>{moment(invoice.due_date).format('MM/DD/YYYY')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <select 
                          value={invoice.status}
                          onChange={(e) => updateStatus(invoice.id, e.target.value)}
                          className="form-select"
                          style={{ width: 'auto', padding: 5, borderRadius: 4 }}
                        >
                          <option value="open">Open</option>
                          <option value="submitted">Submitted</option>
                          <option value="paid">Paid</option>
                          <option value="overdue">Overdue</option>
                        </select>
                        <button
                          onClick={() => confirmDelete(invoice)}
                          title="Delete Invoice"
                          style={{
                            background: '#fee2e2',
                            border: 'none',
                            borderRadius: 4,
                            padding: 6,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <TrashIcon style={{ width: 18, height: 18, color: '#dc2626' }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Generate Invoice Modal */}
      {showGenerateModal && (
        <div className="modal-overlay" onClick={() => setShowGenerateModal(false)} 
             style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} 
               style={{ background: 'white', borderRadius: 8, padding: 24, width: '90%', maxWidth: 500 }}>
            <h2 style={{ marginBottom: 20 }}>Generate Invoice from Service Call</h2>
            
            {uninvoicedCalls.length === 0 ? (
              <div style={{ padding: 20, textAlign: 'center' }}>
                <CheckCircleIcon style={{ width: 48, height: 48, color: '#16a34a', margin: '0 auto 12px' }} />
                <p style={{ color: '#16a34a', fontWeight: 500, marginBottom: 8 }}>All caught up!</p>
                <p style={{ color: '#718096', fontSize: 14 }}>All completed service calls have been invoiced.</p>
              </div>
            ) : (
              <div>
                <p style={{ marginBottom: 16, color: '#718096' }}>
                  Select a completed service call to generate an invoice:
                </p>
                <div style={{ 
                  marginBottom: 12, 
                  padding: '8px 12px', 
                  background: '#f0fdf4', 
                  borderRadius: 6, 
                  fontSize: 13, 
                  color: '#166534',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}>
                  <CheckCircleIcon style={{ width: 16, height: 16 }} />
                  {uninvoicedCalls.length} service call{uninvoicedCalls.length !== 1 ? 's' : ''} ready to invoice
                </div>
                <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                  {uninvoicedCalls.map(call => (
                    <div key={call.id} 
                         style={{ 
                           padding: 12, 
                           marginBottom: 8, 
                           border: '1px solid #e2e8f0', 
                           borderRadius: 6, 
                           cursor: 'pointer',
                           transition: 'all 0.15s'
                         }}
                         onClick={() => generateInvoice(call.id)}
                         onMouseEnter={(e) => {
                           e.currentTarget.style.background = '#f0fdf4';
                           e.currentTarget.style.borderColor = '#86efac';
                         }}
                         onMouseLeave={(e) => {
                           e.currentTarget.style.background = 'white';
                           e.currentTarget.style.borderColor = '#e2e8f0';
                         }}
                    >
                      <div style={{ fontWeight: 600 }}>{call.title}</div>
                      <div style={{ fontSize: 14, color: '#718096' }}>
                        {call.client_name} - {moment.utc(call.scheduled_date).format('MM/DD/YYYY')}
                      </div>
                      {call.cost ? (
                        <div style={{ fontSize: 14, color: '#16a34a', marginTop: 4, fontWeight: 500 }}>
                          Cost: {formatCost(call.cost)}
                        </div>
                      ) : (
                        <div style={{ fontSize: 14, color: '#f59e0b', marginTop: 4 }}>
                          ⚠️ No cost set
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div style={{ marginTop: 20, textAlign: 'right' }}>
              <button onClick={() => setShowGenerateModal(false)} 
                      style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Invoice Modal */}
      {showManualModal && (
        <div className="modal-overlay" onClick={() => setShowManualModal(false)} 
             style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} 
               style={{ background: 'white', borderRadius: 8, padding: 24, width: '90%', maxWidth: 450 }}>
            <h2 style={{ marginBottom: 20 }}>Create Manual Invoice</h2>
            <p style={{ marginBottom: 20, color: '#718096', fontSize: 14 }}>
              Add an existing invoice for tracking purposes.
            </p>
            
            <form onSubmit={createManualInvoice}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14 }}>
                  Client Name *
                </label>
                <input
                  type="text"
                  value={manualForm.client_name}
                  onChange={(e) => setManualForm({ ...manualForm, client_name: e.target.value })}
                  placeholder="Enter client name"
                  style={{ 
                    width: '100%', 
                    padding: '10px 12px', 
                    borderRadius: 6, 
                    border: '1px solid #e2e8f0',
                    fontSize: 14
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14 }}>
                  Amount *
                </label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#718096' }}>$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={manualForm.amount}
                    onChange={(e) => setManualForm({ ...manualForm, amount: e.target.value })}
                    placeholder="0.00"
                    style={{ 
                      width: '100%', 
                      padding: '10px 12px 10px 24px', 
                      borderRadius: 6, 
                      border: '1px solid #e2e8f0',
                      fontSize: 14
                    }}
                    required
                  />
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14 }}>
                  Status
                </label>
                <select
                  value={manualForm.status}
                  onChange={(e) => setManualForm({ ...manualForm, status: e.target.value })}
                  style={{ 
                    width: '100%', 
                    padding: '10px 12px', 
                    borderRadius: 6, 
                    border: '1px solid #e2e8f0',
                    fontSize: 14
                  }}
                >
                  <option value="open">Open</option>
                  <option value="submitted">Submitted</option>
                  <option value="paid">Paid</option>
                  <option value="overdue">Overdue</option>
                </select>
              </div>

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 14 }}>
                  Notes (optional)
                </label>
                <textarea
                  value={manualForm.notes}
                  onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })}
                  placeholder="Add any notes..."
                  rows={3}
                  style={{ 
                    width: '100%', 
                    padding: '10px 12px', 
                    borderRadius: 6, 
                    border: '1px solid #e2e8f0',
                    fontSize: 14,
                    resize: 'vertical'
                  }}
                />
              </div>
              
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button 
                  type="button"
                  onClick={() => setShowManualModal(false)} 
                  style={{ padding: '10px 20px', borderRadius: 6, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={submitting}
                  style={{ 
                    padding: '10px 20px', 
                    borderRadius: 6, 
                    border: 'none', 
                    background: submitting ? '#9ca3af' : '#ea580c', 
                    color: 'white', 
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    fontWeight: 500
                  }}
                >
                  {submitting ? 'Creating...' : 'Create Invoice'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && invoiceToDelete && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)} 
             style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} 
               style={{ background: 'white', borderRadius: 8, padding: 24, width: '90%', maxWidth: 400 }}>
            <h2 style={{ marginBottom: 16, color: '#dc2626' }}>Delete Invoice</h2>
            <p style={{ marginBottom: 8, color: '#374151' }}>
              Are you sure you want to delete this invoice?
            </p>
            <div style={{ 
              padding: 12, 
              background: '#f9fafb', 
              borderRadius: 6, 
              marginBottom: 20 
            }}>
              <div style={{ fontWeight: 600 }}>{invoiceToDelete.invoice_number}</div>
              <div style={{ fontSize: 14, color: '#6b7280' }}>
                {invoiceToDelete.client_name || invoiceToDelete.manual_client_name} - ${invoiceToDelete.total}
              </div>
            </div>
            <p style={{ marginBottom: 20, color: '#dc2626', fontSize: 14 }}>
              ⚠️ This action cannot be undone.
            </p>
            
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button 
                onClick={() => { setShowDeleteModal(false); setInvoiceToDelete(null); }} 
                style={{ padding: '10px 20px', borderRadius: 6, border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button 
                onClick={deleteInvoice}
                disabled={deleting}
                style={{ 
                  padding: '10px 20px', 
                  borderRadius: 6, 
                  border: 'none', 
                  background: deleting ? '#9ca3af' : '#dc2626', 
                  color: 'white', 
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontWeight: 500
                }}
              >
                {deleting ? 'Deleting...' : 'Delete Invoice'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
